import React from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideMemberSearch: React.FC = () => {
  return (
    <GuidePageLayout
      title="要員を検索する"
      description="条件を指定して要員を絞り込み検索します。"
      breadcrumbs={[{ label: '要員管理', path: '/guide/member/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '要員管理画面を開く' },
            {
              label: '検索条件を入力する（全て任意、未入力で全件検索）',
              content: (
                <GuideMockContainer label="検索条件チップ">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <GuideMockChip label="要員名: 田中" color="info" />
                    <GuideMockChip label="雇用形態: 社外" color="secondary" />
                    <GuideMockChip label="パートナー: サンプル" color="warning" />
                    <GuideMockChip label="個人事業主のみ" color="success" />
                    <GuideMockChip label="削除済みを含む" color="default" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「検索」ボタンを押下する' },
            { label: '検索結果が一覧表示される' },
          ]}
        />
      </GuideSection>

      <GuideSection title="検索条件">
        <GuideTable
          headers={['項目名', '形式', '検索方法', '備考']}
          rows={[
            ['要員名', 'テキスト', '前方一致', ''],
            ['要員名（カナ）', 'カタカナテキスト', '前方一致', ''],
            ['雇用形態', 'プルダウン', '完全一致', '社員 / 社外'],
            ['契約形態', 'プルダウン', '完全一致', '社員 / 派遣 / SES / 業務委託'],
            ['パートナー', 'プルダウン', '完全一致', '登録済みパートナーから選択'],
            ['個人事業主', 'チェックボックス', '—', 'ONで個人事業主のみ表示'],
            ['削除済みを含む', 'チェックボックス', '—', 'ONで削除済みも表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="検索結果一覧の表示">
        <GuideTable
          headers={['表示項目', '位置', '説明']}
          rows={[
            ['要員名', 'メイン（左）', '要員の表示名'],
            ['雇用形態Chip', 'メイン（右）', '社員 or 社外を表示'],
            ['削除済Chip', 'メイン（右）', '削除済みの場合のみ表示'],
            [
              'パートナー名 / 部署名 / 要員名カナ',
              'サブテキスト',
              '社外の場合はパートナー名、社員の場合は部署名、いずれもなければカナ名',
            ],
          ]}
        />
        <GuideMockContainer label="リスト項目の表示例">
          <Stack spacing={1.5}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight={500}>
                  田中 太郎
                </Typography>
                <GuideMockChip label="社員" color="primary" size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                開発部
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight={500}>
                  佐藤 花子
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <GuideMockChip label="社外" color="secondary" size="small" />
                  <GuideMockChip label="削除済" color="error" variant="outlined" size="small" />
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                株式会社サンプル
              </Typography>
            </Stack>
          </Stack>
        </GuideMockContainer>
        <GuideTable
          headers={['Chip', '意味', '表示条件', '色']}
          rows={[
            ['社員', '社員（自社雇用）', '雇用形態が EMPLOYEE の場合', '青（primary）'],
            ['社外', '社外（パートナー所属）', '雇用形態が EMPLOYEE 以外の場合', '紫（secondary）'],
            ['削除済', '論理削除された要員', '削除フラグがONの場合', '赤枠（error, outlined）'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>デフォルトでは削除済み要員は非表示です。</GuideInfoBox>
          <GuideInfoBox>表示件数には上限があります。条件を絞って検索してください。</GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideMemberSearch;
